<script setup lang="ts">
import { onMounted, ref } from 'vue'
import Bs2Title from '@/components/common/layout/Bs2Title.vue'
import TextInput from '@/components/common/inputs/TextInput.vue'
import MarkdownWysiwygInput from '@/components/common/inputs/MarkdownWysiwygInput.vue'
import {
  useLegalPagesAdminStore,
  type LegalPageItem,
  type LegalPageDetail,
  type LegalPageStatus,
  type CreateLegalPagePayload,
  type UpdateLegalPagePayload,
  type PublishValidationError,
} from '@/stores/useLegalPagesAdminStore'
import {
  ArchiveBoxIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  PencilSquareIcon,
  PlusIcon,
  XCircleIcon,
} from '@heroicons/vue/24/outline'

const store = useLegalPagesAdminStore()

// ─── Édition (création + update) ────────────────────────────────────────────
const showEditModal = ref(false)
const editingDetail = ref<LegalPageDetail | null>(null) // null en création
const editing = ref(false)
const editError = ref<string | null>(null)
const activeLang = ref<'fr' | 'en'>('fr')

interface EditForm {
  uuid?: string
  key: string
  status: LegalPageStatus
  order: number
  titleFr: string
  bodyFr: string
  titleEn: string
  bodyEn: string
}

const form = ref<EditForm>(buildEmptyForm())

function buildEmptyForm(): EditForm {
  return {
    key: '',
    status: 'DRAFT',
    order: 0,
    titleFr: '',
    bodyFr: '',
    titleEn: '',
    bodyEn: '',
  }
}

function detailToForm(d: LegalPageDetail): EditForm {
  const fr = d.translations.find((t) => t.language === 'fr')
  const en = d.translations.find((t) => t.language === 'en')
  return {
    uuid: d.uuid,
    key: d.key,
    status: d.status,
    order: d.order,
    titleFr: fr?.title ?? '',
    bodyFr: fr?.body ?? '',
    titleEn: en?.title ?? '',
    bodyEn: en?.body ?? '',
  }
}

function formToCreatePayload(f: EditForm): CreateLegalPagePayload {
  const translations = [{ language: 'fr', title: f.titleFr.trim(), body: f.bodyFr.trim() }]
  if (f.titleEn.trim() || f.bodyEn.trim()) {
    translations.push({ language: 'en', title: f.titleEn.trim(), body: f.bodyEn.trim() })
  }
  return {
    key: f.key.trim(),
    status: f.status,
    order: f.order,
    translations,
  }
}

function formToUpdatePayload(f: EditForm): UpdateLegalPagePayload {
  const { key, ...rest } = formToCreatePayload(f)
  void key
  return rest
}

function openCreate() {
  editingDetail.value = null
  form.value = buildEmptyForm()
  editError.value = null
  activeLang.value = 'fr'
  showEditModal.value = true
}

async function openEdit(item: LegalPageItem) {
  editingDetail.value = null
  editError.value = null
  activeLang.value = 'fr'
  showEditModal.value = true
  const detail = await store.fetchDetail(item.uuid)
  if (detail) {
    editingDetail.value = detail
    form.value = detailToForm(detail)
  } else {
    editError.value = 'Impossible de charger le détail'
  }
}

function closeEdit() {
  showEditModal.value = false
  editingDetail.value = null
  editError.value = null
}

async function saveEdit() {
  editing.value = true
  editError.value = null
  try {
    if (!editingDetail.value && !form.value.key.trim()) {
      editError.value = 'La clé (key) est obligatoire.'
      return
    }
    if (!form.value.titleFr.trim() || !form.value.bodyFr.trim()) {
      editError.value = 'Le titre et le contenu FR sont obligatoires.'
      return
    }
    const res = editingDetail.value
      ? await store.update(editingDetail.value.uuid, formToUpdatePayload(form.value))
      : await store.create(formToCreatePayload(form.value))
    if (res.ok) {
      closeEdit()
    } else {
      editError.value = res.message
    }
  } finally {
    editing.value = false
  }
}

// ─── Actions (publish, archive) ─────────────────────────────────────────────
const publishErrors = ref<PublishValidationError[]>([])
const showPublishErrorsModal = ref(false)

async function publish(item: LegalPageItem) {
  const res = await store.publish(item.uuid)
  if (res.ok) {
    if (res.data.ok) {
      alert('Page publiée ✓')
    } else {
      publishErrors.value = res.data.errors ?? []
      showPublishErrorsModal.value = true
    }
  } else {
    alert(res.message)
  }
}

async function archive(item: LegalPageItem) {
  if (!confirm('Archiver cette page ? Elle ne sera plus affichée dans le portail.')) return
  const res = await store.archive(item.uuid)
  if (!res.ok) alert(res.message)
}

// ─── Badges ─────────────────────────────────────────────────────────────────
function statusBadgeClass(s: LegalPageStatus) {
  if (s === 'PUBLISHED') return 'badge-success'
  if (s === 'ARCHIVED') return 'badge-ghost'
  return 'badge-warning'
}

onMounted(async () => {
  await store.fetchAll()
})
</script>

<template>
  <div class="p-6">
    <Bs2Title title="Pages légales" subtitle="Mentions légales, confidentialité, cookies, CGU, sécurité — footer de l'espace client" />

    <div class="flex flex-wrap gap-2 mt-4">
      <router-link to="/dashboard/admin" class="btn btn-outline btn-sm">← Retour dashboard admin</router-link>
      <button class="btn btn-primary btn-sm gap-1" @click="openCreate">
        <PlusIcon class="w-4 h-4" />
        Nouvelle page
      </button>
    </div>

    <div class="mt-6 card bg-base-100 shadow">
      <div class="card-body">
        <div v-if="store.isLoading" class="py-8 text-center">
          <span class="loading loading-spinner loading-lg" />
        </div>
        <div v-else-if="store.items.length === 0" class="py-8 text-center text-base-content/60">
          Aucune page. Créez la première avec le bouton "Nouvelle page".
        </div>
        <div v-else class="overflow-x-auto">
          <table class="table table-sm">
            <thead>
              <tr>
                <th>Statut</th>
                <th>Clé</th>
                <th>Titre (FR)</th>
                <th>Langues</th>
                <th class="text-center">Ordre</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in store.items" :key="item.uuid" class="hover">
                <td>
                  <span class="badge badge-sm" :class="statusBadgeClass(item.status)">{{ item.status }}</span>
                </td>
                <td><span class="font-mono text-xs">{{ item.key }}</span></td>
                <td class="max-w-md">
                  <div class="text-sm truncate" :title="item.titleFr ?? ''">
                    {{ item.titleFr ?? '(pas de trad FR)' }}
                  </div>
                </td>
                <td>
                  <div class="flex gap-1">
                    <span v-for="l in item.availableLanguages" :key="l" class="badge badge-outline badge-xs uppercase">
                      {{ l }}
                    </span>
                  </div>
                </td>
                <td class="text-center tabular-nums">{{ item.order }}</td>
                <td>
                  <div class="flex gap-1">
                    <button class="btn btn-ghost btn-xs" @click="openEdit(item)" title="Éditer">
                      <PencilSquareIcon class="w-3 h-3" />
                    </button>
                    <button
                      v-if="item.status !== 'PUBLISHED'"
                      class="btn btn-ghost btn-xs text-success"
                      @click="publish(item)"
                      title="Publier"
                    >
                      <CheckCircleIcon class="w-3 h-3" />
                    </button>
                    <button
                      v-if="item.status === 'PUBLISHED'"
                      class="btn btn-ghost btn-xs"
                      @click="archive(item)"
                      title="Archiver"
                    >
                      <ArchiveBoxIcon class="w-3 h-3" />
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Modal Création/Édition -->
    <div class="modal" :class="{ 'modal-open': showEditModal }">
      <div class="modal-box max-w-5xl">
        <h3 class="font-bold text-lg flex items-center gap-2">
          <DocumentTextIcon class="w-6 h-6" />
          {{ editingDetail ? `Édition — ${form.titleFr || form.key}` : 'Nouvelle page légale' }}
        </h3>

        <div class="mt-4 space-y-4">
          <!-- Métadonnées -->
          <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div class="form-control col-span-2">
              <label class="label py-1"><span class="label-text text-xs">Clé (key)</span></label>
              <input
                v-model="form.key"
                type="text"
                placeholder="mentions-legales"
                class="input input-bordered input-sm font-mono"
                :disabled="editing || !!editingDetail"
              />
            </div>
            <div class="form-control">
              <label class="label py-1"><span class="label-text text-xs">Statut</span></label>
              <select v-model="form.status" class="select select-bordered select-sm" :disabled="editing">
                <option value="DRAFT">Brouillon</option>
                <option value="PUBLISHED">Publiée</option>
                <option value="ARCHIVED">Archivée</option>
              </select>
            </div>
            <div class="form-control">
              <label class="label py-1"><span class="label-text text-xs">Ordre</span></label>
              <input v-model.number="form.order" type="number" class="input input-bordered input-sm" :disabled="editing" />
            </div>
          </div>

          <!-- Onglets langues -->
          <div role="tablist" class="tabs tabs-bordered">
            <a role="tab" class="tab" :class="{ 'tab-active': activeLang === 'fr' }" @click="activeLang = 'fr'">
              Français <span class="text-error ml-1">*</span>
            </a>
            <a role="tab" class="tab" :class="{ 'tab-active': activeLang === 'en' }" @click="activeLang = 'en'">
              English
            </a>
          </div>

          <!-- FR -->
          <div v-show="activeLang === 'fr'" class="space-y-3">
            <TextInput v-model="form.titleFr" label="Titre" required :disabled="editing" />
            <MarkdownWysiwygInput
              name="bodyFr"
              v-model="form.bodyFr"
              label="Contenu"
              required
              :disabled="editing"
            />
          </div>

          <!-- EN -->
          <div v-show="activeLang === 'en'" class="space-y-3">
            <TextInput v-model="form.titleEn" label="Titre (EN)" :disabled="editing" />
            <MarkdownWysiwygInput
              name="bodyEn"
              v-model="form.bodyEn"
              label="Contenu (EN)"
              :disabled="editing"
            />
            <p class="text-xs text-base-content/50">
              Optionnel — si vide, le portail affiche la version FR.
            </p>
          </div>

          <div v-if="editError" class="alert alert-error text-sm">{{ editError }}</div>
        </div>

        <div class="modal-action">
          <button class="btn" @click="closeEdit" :disabled="editing">Annuler</button>
          <button class="btn btn-primary" @click="saveEdit" :disabled="editing">
            {{ editing ? 'Enregistrement…' : 'Enregistrer' }}
          </button>
        </div>
      </div>
      <div class="modal-backdrop" @click="closeEdit" />
    </div>

    <!-- Modal erreurs de publication -->
    <div class="modal" :class="{ 'modal-open': showPublishErrorsModal }">
      <div class="modal-box max-w-xl">
        <h3 class="font-bold text-lg flex items-center gap-2">
          <XCircleIcon class="w-6 h-6 text-error" />
          Publication impossible
        </h3>
        <p class="mt-3 text-sm">La page ne respecte pas les règles de publication :</p>
        <ul class="mt-3 space-y-2">
          <li v-for="(err, i) in publishErrors" :key="i" class="alert alert-warning text-sm">
            <ExclamationTriangleIcon class="w-4 h-4 shrink-0" />
            <div>
              <div class="font-mono text-xs opacity-70">{{ err.code }}</div>
              <div>{{ err.message }}</div>
            </div>
          </li>
        </ul>
        <div class="modal-action">
          <button class="btn btn-primary" @click="showPublishErrorsModal = false">Fermer</button>
        </div>
      </div>
      <div class="modal-backdrop" @click="showPublishErrorsModal = false" />
    </div>
  </div>
</template>
